import { useState, useEffect, useMemo, useRef } from 'react';

import { Typography } from 'antd';

import { dialogFunction, CustomNotification } from 'common/components';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import isEmpty from 'lodash/isEmpty';

import { getAllRows } from 'pages/qa-spec/utils';
import { apiHandler } from 'utils/api';
import sortByKey from 'utils/sortByKey';
import {
  getProductSpecRequiredFields,
  validateAllRows,
  validateMicrobiologicalCharacteristicGrid,
  validateRequiredFieldProductSpec,
} from 'pages/qa-spec/components/qa-prod-spec/utils';
import { addErrorsToRow } from 'pages/qa-spec/utils';
import { sleep } from 'utils/delay';
import { checkSectionRequired } from 'pages/qa-spec/utils';
import {
  transformBooleanToStringQaSpecValue,
  transformStringToBooleanQaSpecValue,
} from 'pages/qa-spec/utils/transformQaBooleanValue';

import { useQaSpecGrid } from './useQaSpecGrid';
import { useGetMicrobiologicalCharacteristicsQuery } from 'pages/qa-spec/query';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from './useGetQaSpecRequiredFields';
import useCheckSnapshotForRetailer from './useCheckSnapshotForRetailer';

import * as qaSpecServices from 'services/qaSpec';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/qa-spec';

export const useMicrobiological = ({
  productId,
  enumOptions,
  isGridEditing,
}) => {
  const intl = useIntl();
  const gridInst = useRef();

  const [saveLoading, setSaveLoading] = useState(false);

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const isToggleRequiredFields = !isEmpty(requiredFields);

  const microbiologicalRequiredFields = getProductSpecRequiredFields({
    gridInst,
    requiredFields,
    requiredFieldPath:
      'qaSpecification.qaSpecProductSpecification.qaSpecProductSpecsMicrobiologicalCharacteristics'.toLowerCase(),
  });

  const resetGridCallback = async () => {
    if (isToggleRequiredFields) {
      validateAllRows({
        gridInst,
        requiredFields: microbiologicalRequiredFields,
      });
    }
  };

  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const {
    microbiologicalCharacteristicData,
    handleRefetchMicrobiologicalCharacteristics,
  } = useGetMicrobiologicalCharacteristicsQuery({
    productId,
    enabled: !!productId && !isGridEditing,
  });

  const {
    fetchDataLoading,
    addGridRow,
    deleteSelectedGridRow,
    selectGridRow,
    resetGridData,
    rowData,
    selectedRow,
  } = useQaSpecGrid({
    gridInst,
    isGridEditing,
    gridData: microbiologicalCharacteristicData?.microbiologicalCharacteristics,
    resetGridCallback,
  });

  const isSectionRequired = checkSectionRequired(
    requiredFields,
    'microbiological'
  );

  const textEditorRenderer =
    (maxLength = 150) =>
    () => {
      return {
        component: 'TextEditor',
        popup: true,
        params: {
          maxLength: maxLength,
        },
      };
    };

  const columnDefs = useMemo(() => {
    const statusField = {
      minWidth: 50,
      field: 'error',
      displayName: ' ',
      cellRenderer: 'StatusRender',
      type: 'nonEditableColumn',
    };

    let cols = [
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Parameter',
        field: 'parameter',
        width: 200,
        flex: null,
        cellEditorSelector: () => {
          return {
            popup: true,
            component: 'SelectionFreeText',
            params: {
              maxLength: 150,
              values: sortByKey(enumOptions?.parameter, 'enumDisplayName')?.map(
                (item) => ({
                  value: item.enumDisplayName,
                  displayName: item.enumDisplayName,
                })
              ),
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.parameter',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Method',
        field: 'method',
        width: 150,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.method',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Value (Food and Non-Food products)',
        field: 'value',
        width: 350,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.value',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Specification/Description/Target',
        field: 'specification',
        width: 320,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.specification',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Tolerance',
        field: 'tolerance',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.tolerance',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Reject/Fail Criteria',
        field: 'rejectOrFailCriteria',
        width: 200,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.rejectOrFailCriteria',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Ref. Standard',
        minWidth: 180,
        field: 'referenceStandard',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.referenceStandard',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Ref. Method',
        minWidth: 180,
        field: 'referenceMethod',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.referenceMethod',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Test Source',
        field: 'test',
        cellEditorSelector: () => {
          return {
            popup: true,
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Internal', displayName: 'Internal' },
                { value: 'External', displayName: 'External' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.test',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Frequency',
        field: 'frequency',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.frequency',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Release Criteria',
        minWidth: 180,
        field: 'releaseCriteria',
        cellEditorSelector: () => {
          return {
            popup: true,
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Yes', displayName: 'Yes' },
                { value: 'No', displayName: 'No' },
                { value: 'Not Applicable', displayName: 'Not Applicable' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.microbiologicalCharacteristics.releaseCriteria',
        },
      },
    ];

    cols = isToggleRequiredFields ? [statusField, ...cols] : cols;

    return cols.map((fieldItem) => {
      const DEFAULT_WIDTH = 150;

      let initColSetting = {
        type: 'editableColumn',
      };

      return {
        minWidth: DEFAULT_WIDTH,
        ...initColSetting,
        ...fieldItem,
      };
    });
  }, [JSON.stringify(enumOptions), isToggleRequiredFields, isToggleSnapshot]);

  const addNewRow = () => {
    let newRowData = {
      parameter: null,
      specification: null,
      tolerance: null,
      frequency: null,
      releaseCriteria: null,
      value: null,
      referenceStandard: null,
      referenceMethod: null,
      test: null,
    };
    if (isToggleRequiredFields) {
      const errorRequiredFields = microbiologicalRequiredFields?.map(
        (field) => field?.errorMessage
      );
      newRowData = addErrorsToRow(newRowData, errorRequiredFields);
      addGridRow(newRowData);
      return;
    }

    addGridRow();
  };

  const saveData = async (toggleEditMode, analysisNotExecuted) => {
    gridInst.current.api.stopEditing();

    const GRID_VALIDATION_DELAY = 700;
    await sleep(GRID_VALIDATION_DELAY);

    // const isGridHaveError = validateMicrobiologicalCharacteristicGrid({
    //   grid: gridInst,
    // }).checkIfGridHaveError();

    // if (isGridHaveError) {
    //   CustomNotification.error(
    //     `Please resolve the Microbiological Characteristics grid's errors.`
    //   );
    //   return;
    // }
    const allRowData = getAllRows(gridInst);

    if (
      isToggleRequiredFields &&
      microbiologicalRequiredFields?.length > 0 &&
      allRowData?.length <= 0
    ) {
      CustomNotification.error('Please add at least one row data to save');
      return;
    }

    //* clear selected row
    selectGridRow(null);

    setSaveLoading(true);

    const params = {
      productId,
      microbiologicalCharacteristics: allRowData,
      analysisNotExecuted:
        transformStringToBooleanQaSpecValue(analysisNotExecuted),
    };

    const successMessage = intl.formatMessage(
      Messages.productSpecSaveMicrobiologicalSuccess
    );
    const errorMessage = intl.formatMessage(
      Messages.productSpecSaveMicrobiologicalError
    );

    apiHandler({
      service: qaSpecServices.saveQaSpecMicrobiologicalCharacteristic,
      params,
      successMessage,
      errorMessage,
      successCallback: saveSuccessCallback(toggleEditMode),
      onFinally: saveFinally,
    });
  };

  const saveSuccessCallback = (toggleEditMode) => () => {
    toggleEditMode();

    // refetch
    handleRefetchMicrobiologicalCharacteristics();
  };

  const saveFinally = () => {
    setSaveLoading(false);
  };

  const deleteData = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the selected item?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: onDeleteData,
    });
  };

  const onDeleteData = () => {
    deleteSelectedGridRow();
  };

  const selectRow = () => {
    selectGridRow();
  };

  const cancelEdit = () => {
    resetGridData();
  };

  const handleValidateRequiredFieldForSupplier = ({ event }) => {
    microbiologicalRequiredFields?.forEach((item) => {
      if (item?.fieldName === event.column.colId)
        validateRequiredFieldProductSpec({
          gridInst,
          event,
          requiredFieldError: item.errorMessage,
        });
    });
  };

  const onCellEditingStopped = async (event) => {
    if (isToggleRequiredFields) {
      handleValidateRequiredFieldForSupplier({
        event,
      });
    }
  };

  useEffect(() => {
    const handleValidateAllRowDataForSupplier = async () => {
      if (rowData && rowData?.length > 0 && isToggleRequiredFields) {
        validateAllRows({
          gridInst,
          requiredFields: microbiologicalRequiredFields,
        });
      }
    };

    handleValidateAllRowDataForSupplier();
  }, [
    isToggleRequiredFields,
    microbiologicalRequiredFields,
    JSON.stringify(rowData),
  ]);

  const gridTitle = (
    <>
      {isToggleRequiredFields && isSectionRequired && (
        <>
          <Typography.Text type='danger'>*</Typography.Text>{' '}
        </>
      )}
      Microbiological Characteristics
    </>
  );

  return {
    gridInst,
    analysisNotExecuted: transformBooleanToStringQaSpecValue(
      microbiologicalCharacteristicData?.analysisNotExecuted
    ),
    rowData,
    columnDefs,
    addNewRow,
    saveData,
    deleteData,
    selectRow,
    selectedRow,
    cancelEdit,
    loading: fetchDataLoading || saveLoading,
    onCellEditingStopped,
    isToggleRequiredFields,
    gridTitle,
  };
};
